import React, { FC, useState } from 'react';
import { Trans } from 'react-i18next';
import { Box, Button, Grid, Link, Paper, Typography } from '@material-ui/core';
import { createStyles, withStyles, Theme, WithStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import StarIcon from '@material-ui/icons/Star';

import { FeedKey, PublicUserProfile } from 'api';
import { getFullName } from 'helpers';
import { useFeatures } from 'hooks';
import { generateLink } from 'Links';
import {
  CustomSVG,
  FollowToggle,
  IFollowToggleRenderProps,
  Numeric,
  ToggleButton,
  UserAvatar,
} from 'components';

interface IProps extends WithStyles<typeof styles> {
  user: PublicUserProfile;
}

const styles = (theme: Theme) =>
  createStyles({
    paper: {
      padding: theme.spacing(2),
    },
    avatar: {
      marginBottom: 12,
    },
    text: {
      display: 'flex',
      alignItems: 'center',
    },
    textIcon: {
      marginRight: theme.spacing(0.5),
    },
    follow: {
      justifyContent: 'space-between',
    },
    view: {
      justifyContent: 'center',
    },
    button: {
      display: 'flex',
      padding: '5px 8px 5px 16px',
      marginTop: theme.spacing(2),
      color: theme.custom.palette.grey2,
      borderColor: theme.palette.action.disabled,
      transition: 'all 300ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',

      '&:hover': {
        backgroundColor: 'transparent',
      },

      '&$disabled': {
        color: theme.custom.palette.grey2,

        '&:hover': {
          '&$notFollowing': {
            color: theme.custom.palette.black2,
          },
        },

        '&$notFollowing': {
          borderColor: theme.palette.secondary.main,
        },
      },
    },
    disabled: {},
    notFollowing: {
      borderColor: theme.palette.secondary.main,

      '&:hover': {
        color: theme.custom.palette.black2,
      },
    },
  });

const UserCard: FC<IProps> = ({ classes, user }) => {
  const [hovered, setHovered] = useState(false);
  const { leaderboard } = useFeatures();

  function renderFollowButton(followToggleProps: IFollowToggleRenderProps) {
    const { isFollowing, loading, onClick } = followToggleProps;

    const i18nKey = isFollowing ? 'common:following' : 'common:follow';
    const buttonClasses = classNames(classes.button, classes.follow, {
      [classes.notFollowing]: !isFollowing,
    });

    const buttonProps = {
      disabled: loading,
      className: buttonClasses,
      classes: { root: buttonClasses, disabled: classes.disabled },
      onMouseEnter: () => setHovered(true),
      onMouseLeave: () => setHovered(false),
    };

    return (
      <Button fullWidth onClick={onClick} variant="outlined" {...buttonProps}>
        <Trans i18nKey={i18nKey} />
        <ToggleButton parentHovered={hovered} size={24} selected={isFollowing} />
      </Button>
    );
  }

  function renderProfileButton() {
    const buttonClasses = classNames(classes.button, classes.view);

    return (
      <Button
        component={generateLink(`/profile/${user.id}`)}
        className={buttonClasses}
        fullWidth
        variant="outlined">
        <Trans i18nKey="components:viewProfile" />
      </Button>
    );
  }

  function renderFollowToggle() {
    return (
      <FollowToggle source={user} type={FeedKey.User}>
        {followToggleProps =>
          followToggleProps.disableToggle
            ? renderProfileButton()
            : renderFollowButton(followToggleProps)
        }
      </FollowToggle>
    );
  }

  return (
    <Grid item xs={12} sm={6} md={4} lg={3}>
      <Paper className={classes.paper}>
        <Grid container direction="column" alignItems="center" wrap="nowrap">
          <Grid item xs>
            <UserAvatar link className={classes.avatar} user={user} size={100} />
          </Grid>
          <Grid item xs>
            <Box textOverflow="ellipsis" whiteSpace="noWrap" overflow="hidden">
              <Link component={generateLink(`/profile/${user.id}`)} variant="subtitle2">
                {getFullName(user)}
              </Link>
            </Box>
          </Grid>
          <Grid item xs>
            <Box textOverflow="ellipsis" whiteSpace="noWrap" overflow="hidden">
              <Typography gutterBottom noWrap>
                {user.job_title}
              </Typography>
            </Box>
          </Grid>
          <Grid item xs>
            {!leaderboard && (
              <Typography className={classes.text}>
                <CustomSVG className={classes.textIcon} name="following" size={12} />
                <Numeric value={user.follower_count} />
              </Typography>
            )}
            {leaderboard && (
              <Typography className={classes.text}>
                <StarIcon fontSize="inherit" color="secondary" className={classes.textIcon} />
                <Numeric value={user.karma} />
              </Typography>
            )}
          </Grid>
          {renderFollowToggle()}
        </Grid>
      </Paper>
    </Grid>
  );
};

export default withStyles(styles)(UserCard);
